import { useState, type FormEvent } from 'react';
import { useSession } from '../../lib/auth/session.js';
import {
  useConferenceRooms, useSaveConferenceRoom, useDeleteConferenceRoom,
  type ConferenceRoom,
} from '../../lib/api/hooks-tms.js';
import { TableSkeleton, Empty, ErrorBanner, SubHead, SectionHead, EditorHead } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { Toggle } from '../../components/ui/Form.js';
import { ApiClientError } from '../../lib/api/client.js';
import { confirmAction } from '../../components/ui/Confirm.js';

/**
 * Conference rooms.
 *
 * Everyone dials in from inside the account, so unlike most of the call-routing
 * features this one works today without a carrier — there is nothing here that
 * waits on a trunk.
 */

const PROFILES = [
  { key: 'default', label: 'Standard (8 kHz)', hint: 'Matches what a desk phone carries.' },
  { key: 'wideband', label: 'Wideband (16 kHz)', hint: 'Better, if every participant supports it.' },
  { key: 'ultrawideband', label: 'Ultra-wideband (32 kHz)', hint: 'Softphones and headsets only.' },
  { key: 'cdquality', label: 'CD quality (48 kHz)', hint: 'Highest CPU cost per room.' },
] as const;

export function ConferenceRoomsTab({ tenantId }: { tenantId: string }) {
  // Creating, editing and deleting need queue:write, as the API requires (permission sweep, 19 September 2026).
  const mayWrite = useSession((s) => s.can)('queue:write');
  const rooms = useConferenceRooms(tenantId);
  const del = useDeleteConferenceRoom(tenantId);
  const [editing, setEditing] = useState<ConferenceRoom | null>(null);
  const [creating, setCreating] = useState(false);

  const list = rooms.data?.data ?? [];

  // An editor takes the list's place, so there is one thing on the page (owner, 29 September 2026).
  if (creating || editing) {
    return (
      <>
      <EditorHead back="Conference rooms" onBack={() => { setCreating(false); setEditing(null); }}
        title={editing ? editing.name : 'New conference room'} />
      <RoomForm
        key={editing?.id ?? 'new'}
        tenantId={tenantId} existing={editing}
        onDone={() => { setCreating(false); setEditing(null); }}
      />
      </>
    );
  }

  return (
    <>
      <SectionHead title="Conference rooms"
        sub="A number people dial to meet. Works on internal calls today."
        action={mayWrite && (
        <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
          onClick={() => { setCreating(true); setEditing(null); }}>
          <Icon name="plus" size={13} />
          New room
        </button>
        )} />

      {rooms.error && <ErrorBanner error={rooms.error} />}


      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <colgroup>
              <col /><col style={{ width: 110 }} /><col style={{ width: 150 }} />
              <col style={{ width: 190 }} /><col style={{ width: 90 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Room</th>
                <th>Dial</th>
                <th>Entry</th>
                <th>Behaviour</th>
                <th />
              </tr>
            </thead>
            {rooms.data === undefined ? <TableSkeleton query={rooms} cols={5} /> : (
              <tbody>
                {list.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div className="arc-stack">
                        <span style={{ fontWeight: 550 }}>{r.name}</span>
                        <span className="arc-subtle" style={{ fontSize: 11 }}>
                          {PROFILES.find((p) => p.key === r.profile)?.label ?? r.profile}
                          {r.max_members > 0 ? ` · up to ${r.max_members}` : ''}
                        </span>
                      </div>
                    </td>
                    <td>
                      {r.extension
                        ? <span className="arc-ext-num">{r.extension}</span>
                        : <span className="arc-subtle">no code</span>}
                    </td>
                    <td>
                      <div className="arc-stack">
                        <span className={r.pin ? undefined : 'arc-subtle'}>
                          {r.pin ? 'PIN required' : 'Open to anyone'}
                        </span>
                        {/* The moderator entrance is the room's own code with 9
                            appended — the same fact the dialplan encodes, said
                            where somebody has to tell a chairperson what to dial. */}
                        {r.moderator_pin && r.extension && (
                          <span className="arc-subtle" style={{ fontSize: 11 }}>
                            chair dials {r.extension}9
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      {r.wait_for_moderator || r.end_with_moderator || r.record_room ? (
                        <span style={{ fontSize: 12.5 }}>
                          {[
                            r.wait_for_moderator && 'waits for chair',
                            r.end_with_moderator && 'ends with chair',
                            r.record_room && 'recorded',
                          ].filter(Boolean).join(', ')}
                        </span>
                      ) : (
                        <span className="arc-subtle">—</span>
                      )}
                    </td>
                    <td className="arc-td-right">
                      <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                        {mayWrite && (
                        <RowAction kind="edit" label={`Edit ${r.name}`}
                          onClick={() => { setEditing(r); setCreating(false); }} />
                        )}
                        {mayWrite && (
                        <RowAction kind="delete" label={`Delete ${r.name}`}
                          onClick={() => {
                            void confirmAction({
                              title: `Delete the "${r.name}" room?`,
                              body: `Anyone dialling ${r.extension ?? 'it'} will no longer reach a conference.`,
                              action: 'Delete', danger: true,
                            }).then((ok) => { if (ok) del.mutate(r.id); });
                          }} />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {rooms.data !== undefined && list.length === 0 && (
          <Empty icon="users" title="No conference rooms"
            hint="Create one and give it a number for people to dial." />
        )}
      </div>
    </>
  );
}

function RoomForm({ tenantId, existing, onDone }: {
  tenantId: string; existing: ConferenceRoom | null; onDone: () => void;
}) {
  const save = useSaveConferenceRoom(tenantId);
  const [name, setName] = useState(existing?.name ?? '');
  const [extension, setExtension] = useState(existing?.extension ?? '');
  const [pin, setPin] = useState(existing?.pin ?? '');
  const [modPin, setModPin] = useState(existing?.moderator_pin ?? '');
  const [waitMod, setWaitMod] = useState(existing?.wait_for_moderator ?? false);
  const [endMod, setEndMod] = useState(existing?.end_with_moderator ?? false);
  const [announce, setAnnounce] = useState(existing?.announce_join ?? true);
  const [record, setRecord] = useState(existing?.record_room ?? false);
  const [maxMembers, setMaxMembers] = useState(String(existing?.max_members ?? 0));
  const [profile, setProfile] = useState(existing?.profile ?? 'default');

  const err = save.error instanceof ApiClientError ? save.error : null;
  const fieldErr = err?.fieldErrors ?? {};

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate({
          ...(existing ? { id: existing.id } : {}),
          name,
          extension: extension || null,
          pin: pin || null,
          moderator_pin: modPin || null,
          wait_for_moderator: waitMod,
          end_with_moderator: endMod,
          announce_join: announce,
          announce_leave: false,
          moh_when_alone: true,
          record_room: record,
          max_members: Number(maxMembers) || 0,
          profile,
        }, { onSuccess: onDone });
      }}>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cr-name">Name</label>
          <input id="cr-name" className="arc-input" value={name} required
            onChange={(e) => setName(e.target.value)} placeholder="Monday standup" />
          {fieldErr['name'] && <div className="arc-error">{fieldErr['name']}</div>}
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cr-ext">Dial code</label>
          <input id="cr-ext" className="arc-input arc-mono" value={extension}
            onChange={(e) => setExtension(e.target.value)} placeholder="600" />
          {fieldErr['extension'] && <div className="arc-error">{fieldErr['extension']}</div>}
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cr-pin">PIN</label>
          <input id="cr-pin" className="arc-input arc-mono" value={pin} inputMode="numeric"
            onChange={(e) => setPin(e.target.value)} placeholder="optional" />
          {fieldErr['pin'] && <div className="arc-error">{fieldErr['pin']}</div>}
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cr-mod">Chairperson PIN</label>
          <input id="cr-mod" className="arc-input arc-mono" value={modPin} inputMode="numeric"
            onChange={(e) => setModPin(e.target.value)} placeholder="optional" />
          {fieldErr['moderator_pin'] && <div className="arc-error">{fieldErr['moderator_pin']}</div>}
        </div>
      </div>

      {/*
        A PIN here is a meeting secret, not a credential.
        It goes into a calendar invitation and gets read aloud over a
        speakerphone, so it is stored in the clear and must never be reused as
        a password. Saying so where somebody is choosing one is the only place
        the warning is useful.
      */}
      <p className="arc-muted" style={{ fontSize: 12, marginTop: -4, marginBottom: 14 }}>
        PINs are shared meeting secrets — they go in calendar invitations and get
        read aloud. Treat them as such; never reuse a password here.
        {modPin && extension && (
          <> The chairperson dials <strong className="arc-mono">{extension}9</strong> instead
          of {extension}.</>
        )}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cr-profile">Audio quality</label>
          <select id="cr-profile" className="arc-select" value={profile}
            onChange={(e) => setProfile(e.target.value)}>
            {PROFILES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
          <span className="arc-hint">
            {PROFILES.find((p) => p.key === profile)?.hint}
          </span>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="cr-max">Maximum participants</label>
          <input id="cr-max" className="arc-input" value={maxMembers} inputMode="numeric"
            onChange={(e) => setMaxMembers(e.target.value)} />
          <span className="arc-hint">0 uses the account&apos;s channel limit.</span>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
        <Toggle id="cr-announce" label="Announce people joining"
          checked={announce} onChange={setAnnounce} />
        <Toggle id="cr-wait" label="Nobody hears anything until the chairperson arrives"
          checked={waitMod} onChange={setWaitMod} />
        <Toggle id="cr-end" label="End the meeting when the chairperson leaves"
          checked={endMod} onChange={setEndMod} />
        <Toggle id="cr-rec" label="Record the meeting"
          checked={record} onChange={setRecord} />
      </div>

      {/* A refusal that names no field (a dial code already taken, no permission) was shown nowhere:
          the button just stopped spinning (UX review, 21 October 2026). */}
      {save.error && Object.keys(fieldErr).length === 0 && <ErrorBanner error={save.error} />}
      <div className="arc-row" style={{ gap: 8 }}>
        <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create room'}
        </button>
        <button type="button" className="arc-btn arc-btn-ghost" onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}
